import { useState, useEffect, lazy, Suspense } from 'react';
import {
  useNavigate,
  useLocation,
  Routes,
  Route,
  BrowserRouter,
} from 'react-router-dom';

import PlayBar from './components/PlayBar';
import ProgressBar from './components/ProgressBar';
import NavBar from './components/NavBar';
import LimitModal from './components/LimitModal';
import WelcomeModal from './components/WelcomeModal';
import Home from './pages/Home';
import {
  Profile,
  ItemPage,
  PlaylistEditor,
  Welcome,
  AuthCallback,
  SubCallback,
  PlaylistRedirect,
  Sentences,
  Queue,
  History,
  Favorites,
  Category,
  Stylesheet,
  BuildersCanon,
} from './pages';

// Lazy-load admin routes (chart.js ~493KB only used here)
const Admin = lazy(() => import('./pages/admin'));
const AdminHome = lazy(() => import('./pages/admin/Home'));
const AdminCurator = lazy(() => import('./pages/admin/Curator'));
const AdminWorkers = lazy(() => import('./pages/admin/Workers'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminUserDetail = lazy(() => import('./pages/admin/UserDetail'));
const AdminVoices = lazy(() => import('./pages/admin/Voices'));
import { ToastProvider } from './contexts/ToastContext';
import { AuthProvider } from './contexts/AuthContext';
import { UIProvider } from './contexts/UIContext';
import Toast from './components/Toast';
import { PlayerProvider } from './contexts/PlayerContext';
import useAuth from './contexts/useAuth';
import usePlayer from './contexts/usePlayer';
import MediaSession from './components/MediaSession';
import { useNowPlayingVisible } from './hooks/useNowPlayingVisible';

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { nowPlaying, currentPlaylist, limitModal, dismissLimitModal } =
    usePlayer();
  const [showWelcome, setShowWelcome] = useState(false);
  const isNowPlayingVisible = useNowPlayingVisible();

  // Show welcome modal for first-time anonymous users
  useEffect(() => {
    if (authLoading) return;
    if (!user) return;

    // Only show for anonymous users who haven't visited before
    const hasVisited = localStorage.getItem('hasVisited');
    if (!user.email && !hasVisited) {
      setShowWelcome(true);
    }
  }, [authLoading, user]);

  const dismissWelcome = () => {
    localStorage.setItem('hasVisited', 'true');
    setShowWelcome(false);
  };

  // Hide PlayBar when viewing the now-playing item (unified view has its own ItemPlayBar)
  // Also hide on the currently-playing playlist page
  const isOnNowPlayingPage =
    nowPlaying && location.pathname === `/id/${nowPlaying.id}`;
  // Check if we're on the current playlist's page
  const isOnCurrentPlaylistPage =
    currentPlaylist && location.pathname === `/id/${currentPlaylist.id}`;
  const hidePlayBar = isOnNowPlayingPage || isOnCurrentPlaylistPage;

  // Wait for auth before rendering anything
  if (authLoading) {
    return <div className="app-container" />;
  }

  return (
    <div className="app-container">
      {user?.isAdmin && (
        <button
          onClick={() => navigate('/admin')}
          style={{
            position: 'absolute',
            top: '20px',
            left: 0,
            width: '40px',
            height: '40px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            zIndex: 10,
          }}
          aria-label="Admin"
        />
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        }}
      >
        <NavBar />
        <Routes>
          {/* Note: All page elements must have className="page" for layout to work */}
          <Route path="/" element={<Home />} />
          <Route path="/queue" element={<Queue />} />
          <Route path="/history" element={<History />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/category/:name" element={<Category />} />
          <Route path="/profile" element={<Profile />} />
          <Route
            path="/admin"
            element={
              <Suspense fallback={null}>
                <Admin />
              </Suspense>
            }
          >
            <Route index element={<AdminHome />} />
            <Route path="curator" element={<AdminCurator />} />
            <Route path="workers" element={<AdminWorkers />} />
            <Route path="voices" element={<AdminVoices />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:userId" element={<AdminUserDetail />} />
          </Route>
          <Route path="/playlist/new" element={<PlaylistEditor />} />
          <Route path="/playlist/edit/:id" element={<PlaylistEditor />} />
          <Route path="/id/:id/sentences" element={<Sentences />} />
          <Route path="/id/:id" element={<ItemPage />} />
          <Route path="/paulgraham" element={<PlaylistRedirect />} />
          <Route path="/waitbutwhy" element={<PlaylistRedirect />} />
          <Route path="/gwern" element={<PlaylistRedirect />} />
          <Route path="/accelerando" element={<PlaylistRedirect />} />
          <Route path="/builder" element={<BuildersCanon />} />
          <Route path="/stylesheet" element={<Stylesheet />} />
          <Route
            path="*"
            element={
              <div
                className="page"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <p style={{ color: 'var(--subtle)' }}>
                  Whoops, there's nothing here!
                </p>
              </div>
            }
          />
        </Routes>
        {!hidePlayBar && (
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              paddingBottom: 'env(safe-area-inset-bottom)',
              backgroundColor: 'var(--card-bg)',
              transform: isNowPlayingVisible
                ? 'translateY(100%)'
                : 'translateY(0)',
              transition: 'transform 0.3s ease',
              zIndex: 100,
            }}
          >
            <div
              style={{
                maxWidth: '600px',
                margin: '0 auto',
              }}
            >
              <ProgressBar />
              <PlayBar
                onOpen={() => nowPlaying && navigate(`/id/${nowPlaying.id}`)}
              />
            </div>
          </div>
        )}
      </div>
      {limitModal && (
        <LimitModal type={limitModal} onDismiss={dismissLimitModal} />
      )}
      {showWelcome && <WelcomeModal onDismiss={dismissWelcome} />}
    </div>
  );
};

const App = () => (
  <ToastProvider>
    <BrowserRouter>
      <Routes>
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/auth-callback" element={<AuthCallback />} />
        <Route path="/sub-callback" element={<SubCallback />} />
        <Route
          path="/*"
          element={
            <UIProvider>
              <AuthProvider>
                <PlayerProvider>
                  {'mediaSession' in navigator && <MediaSession />}
                  <AppLayout />
                </PlayerProvider>
              </AuthProvider>
            </UIProvider>
          }
        />
      </Routes>
    </BrowserRouter>
    <Toast />
  </ToastProvider>
);

export default App;
